import { Grid, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useInvoices } from "../../services/bankingService";
import TableService from "../../services/tableService";

import InvoicingNavigationBar from "../InvoicingInvestmentBillPage/InvoicingNavigrationBar";

import { useTableFieldsConfig, TABLE_NAME } from "./PageTableFieldsConfig";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { centerVericalAlignIconStyle } from "../../helpers/styles";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { InvoiceImportListDialog } from "./ImportInvoicesDialog";

import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import { DateRangeFilter } from "../../components/common/DateRangeFilter/DateRangeFilter";

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";

import { DeleteMassInvoiceButton } from "./DeleteMassInvoiceButton";
import { ChangeInvoiceIsBookedButton } from "./ChangeInvoiceIsBookedButton";

export default function InvoicingInvoicesPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.INVESTMENT_DPI, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT]);

    if (!hasAccess){
        return null
    }

    const { pageName } = props;

    const { t } = useTranslation();
    const TABLE_FIELDS_CONFIG = useTableFieldsConfig();
    
    const TABLE_CONFIGS = [{ name: TABLE_NAME, config: TABLE_FIELDS_CONFIG }];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [checkedAll, setCheckedAll] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const { data: transferData, refetch, isLoading } = useInvoices(searchParams);
    useEffect(() => {
        if (transferData?.results) {
            setDataLocal(transferData.results);
            setRecordsCount(transferData.count);
        }
    }, [transferData, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const trnasferDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[TABLE_NAME] || [],
        );
    }, [TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const { openDialog: openImportInvoiceDialog } = useDialogBetter(); 
    const handleOpenImportInvoiceDialog = (e) => {
        openImportInvoiceDialog({
            Component: InvoiceImportListDialog,
        });
    };

    const getSeletedIds = () =>
    dataLocal
        .filter((rowData) => rowData.selected)
        .map((rowData) => rowData.id);

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"bankings/invoices/export/"}
            exportToFileSearchParams={searchParams}
            exportToFileHeaders={trnasferDataExportHeaders}
            exportToFileFileName={`${t("bankings.exportInvoicesFileName")}.xlsx`}
            showDetailsIcon={false}
            checkedAll={checkedAll}
            setCheckedAll={setCheckedAll}
            showCheckbox={true}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.invoicing_invoices")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    < InvoicingNavigationBar />
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                        extraButtonList={[
                            <InvestmentFilter
                                filterName={"transfer_invest"}
                                searchParams={searchParams}
                                setSearchParams={setSearchParams}
                                InputLabelStyle={{marginTop: "-6px"}}
                                maxHeight={"40px"}
                            />,
                            //     <DateRangeFilter
                            //     labelFrom={t("bankings.transfer_date_from")}
                            //     labelTo={t("bankings.transfer_date_to")}
                            //     filterName={"transfer_date"}
                            //     searchParams={searchParams}
                            //     setSearchParams={setSearchParams}
                            // />,
                            <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                startIcon={<FileUploadOutlinedIcon sx={centerVericalAlignIconStyle} />}
                                onClick={handleOpenImportInvoiceDialog}
                                sx={{bgcolor: "primary.light"}}
                            >
                                {t("bankings.importInvoices")}
                            </Button>,
                            <DeleteMassInvoiceButton selectedInvoices={getSeletedIds()}/>
                            // <ChangeInvoiceIsBookedButton selectedInvoices={getSeletedIds()}/>
                        ]}
                        // extraButtonListMdGrid={[2,3,2]}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>
                        {tableComponent()}
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
